import { ref, watch, computed } from 'vue'
import * as Sentry from '@sentry/vue'

// Shared reactive state
const isAmountsHidden = ref(localStorage.getItem('fin_hide_amounts') === 'true')
const isDarkMode = ref(localStorage.getItem('fin_theme') !== 'light') // Default to true (dark) if not explicitly set to light
const userData = ref(null)
const impersonatedUser = ref(localStorage.getItem('impersonated_user'))
const pendingCount = ref(0)
const categories = ref([])

// Persistence
watch(impersonatedUser, (val) => {
  if (val) {
    localStorage.setItem('impersonated_user', val)
  } else {
    localStorage.removeItem('impersonated_user')
  }
})

// Sentry context sync — id only: no email nor name sent to Sentry (same rule as send_default_pii: false on the backend)
watch(userData, (val) => {
  if (import.meta.env.VITE_SENTRY_DSN && import.meta.env.PROD) {
    if (val) {
      Sentry.setUser({ id: val.id?.toString() })
    } else {
      Sentry.setUser(null)
    }
  }
})

watch(isAmountsHidden, (val) => {
  localStorage.setItem('fin_hide_amounts', val)
})

watch(isDarkMode, (val) => {
  localStorage.setItem('fin_theme', val ? 'dark' : 'light')
  if (val) {
    document.documentElement.classList.add('dark')
  } else {
    document.documentElement.classList.remove('dark')
  }
}, { immediate: true })

export function useGlobalState() {
  const getAuthHeader = () => {
    const headers = { 'Authorization': `Bearer ${localStorage.getItem('apiToken')}` };
    if (impersonatedUser.value) {
      headers['X-Switch-User'] = impersonatedUser.value;
    }
    return headers;
  }

  const startImpersonation = async (email) => {
    impersonatedUser.value = email;
    await fetchUser();
  }

  const stopImpersonation = async () => {
    impersonatedUser.value = null;
    await fetchUser();
  }

  const toggleAmounts = () => {
    isAmountsHidden.value = !isAmountsHidden.value
  }

  const fetchUser = async () => {
    try {
      const res = await fetch('/api/me', { headers: getAuthHeader() })
      if (res.ok) userData.value = await res.json()
    } catch (e) {}
  }

  const fetchPendingCount = async () => {
    try {
      const [movRes, subRes] = await Promise.all([
        fetch('/api/movements/pending/count', { headers: getAuthHeader() }),
        fetch('/api/subscriptions/pending-alerts', { headers: getAuthHeader() })
      ])
      
      let total = 0
      if (movRes.ok) {
        const movData = await movRes.json()
        total += movData.count
      }
      if (subRes.ok) {
        const subData = await subRes.json()
        total += subData.length
      }
      pendingCount.value = total
    } catch (e) {}
  }

  const fetchCategories = async () => {
    try {
      const res = await fetch('/api/investments/categories', { headers: getAuthHeader() })
      if (res.ok) categories.value = await res.json()
    } catch (e) {}
  }

  const userDisplayName = computed(() => {
    if (!userData.value) return 'Chargement...'
    const full = `${userData.value.firstName || ''} ${userData.value.lastName || ''}`.trim()
    return full || userData.value.email
  })

  const mainCategories = computed(() => categories.value.filter(c => c.parentId === null))

  return {
    isAmountsHidden,
    isDarkMode,
    toggleAmounts,
    toggleDarkMode: () => { isDarkMode.value = !isDarkMode.value },
    userData,
    userDisplayName,
    pendingCount,
    categories,
    mainCategories,
    fetchUser,
    fetchPendingCount,
    fetchCategories,
    getAuthHeader,
    impersonatedUser,
    startImpersonation,
    stopImpersonation
  }
}
